import { DOCUMENT, Location } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, input, NgZone, output, untracked, ViewEncapsulation } from '@angular/core';
import { TooltipModule } from '@jms/ui/ui.tooltip';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import type { EntityPanelKind } from '@workbuddy/services/entities';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { DetailSurfaceRail } from '@workbuddy/ui/v2/surface.engine/surfaces/detail/detail.surface.rail';
import { surfaceBusyTooltip } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.busy';
import { DETAIL_PANE_BACK } from '../../../detail.pane/detail.pane.back';
import { DetailPaneNavigation } from '../../../detail.pane/detail.pane.navigation';
import { BoardPanelLayoutState } from '../../state';
import type { BoardPanelPresentation } from '../../state/board.panel.presentation';
import type { BoardStepPosition } from '../../state/board.panel.stepping';
import { BOARD_PANEL_NO_EXPAND, BoardPanelExpand } from './board.panel.expand';
import { BOARD_PANEL_HOST, BoardPanelHost, BoardPanelRefreshScope, BoardPanelRowPatch } from './board.panel.host';
import { boardPanelRecordPageHref } from './board.panel.record.page';
import { boardPanelBridgeStyles, boardPanelStyleId } from './board.panel.theme';

/**
 * Inline panel frame for a board's selected record. Pure chrome: width, pinned/floating, a close
 * control, and one projected content slot.
 *
 * Deliberately NOT WbSurfaceRailComponent, and deliberately not consuming it. That component owns
 * a tab strip. A preview-mounted surface brings its own tabs — the financial-document
 *    host mounts wb-surface-rail itself, gated on tab count only — so a panel rendering tabs would
 *    produce two nested strips and two competing active-tab states. This renders NO tab chrome.
 *
 * Host is display:contents so the panel sits in the board's own flex row rather than overlaying it.
 */
@Component({
  selector: 'board-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonIconComponent, TooltipModule],
  templateUrl: './board.panel.component.html',
  styleUrls: ['./board.panel.component.less'],
  host: {
    '[class.is-quickview]': "$kind() === 'quickview'",
    '[class.is-preview]': "$kind() === 'preview'",
    '[class.is-floating]': "!$pinned() && $presentation() === 'side'",
    '[class.is-dialog]': "$presentation() === 'dialog'",
  },
  // Provided here rather than on the board so only projected panel content can inject it — the
  // table, cells and renderers have no business closing the panel.
  providers: [
    { provide: BOARD_PANEL_HOST, useExisting: BoardPanelComponent },
    // A panel starts an independent detail tree. Inherited rail/Back targets belong to the
    // enclosing inspector or pane; letting them through sends this panel's controls to its parent.
    // Descendant detail hosts and panes still provide their own rail and child navigation.
    { provide: DetailSurfaceRail, useValue: null },
    { provide: DetailPaneNavigation, useValue: null },
    { provide: DETAIL_PANE_BACK, useValue: null },
  ],
})
export class BoardPanelComponent implements BoardPanelHost {

  private readonly document = inject(DOCUMENT);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly panelLayout = inject(BoardPanelLayoutState);
  private readonly zone = inject(NgZone);
  private readonly location = inject(Location);

  public $kind = input.required<EntityPanelKind>({ alias: 'kind' });
  /** True = this chrome draws title/pin/close. False = resizable box only (Task Detail / w-page owns pin). */
  public $ownsHeaderInput = input<boolean | undefined>(undefined, { alias: 'ownsHeader' });
  /** The record's name; falls back to "<Entity> Details" upstream when the record has none. */
  public $title = input('', { alias: 'title' });
  /** The record's entity type, shown on hovering the title so the name stands alone. */
  public $titleTooltip = input('', { alias: 'titleTooltip' });
  /** Pinned sits in layout and narrows the board; floating overlays it. Preview is always pinned. */
  public $pinned = input(true, { alias: 'pinned' });
  /** Shown when nothing is selected. The slot still reserves its width. */
  public $empty = input(false, { alias: 'empty' });

  /** Off for a panel the board cannot resize (floating full-bleed on a narrow viewport). */
  public $resizable = input(true, { alias: 'resizable' });

  /** Same frame either way; the board decides which and mounts the dialog. */
  public $presentation = input<BoardPanelPresentation>('side', { alias: 'presentation' });
  /** ⤢ / ⤡, resolved by the board from the entity's preview and edit operations. */
  public $expand = input<BoardPanelExpand>(BOARD_PANEL_NO_EXPAND, { alias: 'expand' });
  /** ↗ target; null hides it. */
  public $recordPageUrl = input<string | null>(null, { alias: 'recordPageUrl' });
  public $canStep = input(false, { alias: 'canStep' });
  public $stepPosition = input<BoardStepPosition | null>(null, { alias: 'stepPosition' });
  /** The content has an upload in flight; the board guards leaving, so the controls stay live and only say why they pause. */
  public $busy = input(false, { alias: 'busy' });

  public closed = output<void>();
  /** Pinning changes the board's width, so the board owns the state — this only reports the click. */
  public pinnedChange = output<boolean>();

  // Raw pointer events: clamping needs the manifest bounds and the board's rect, both of which the
  // board has and the panel does not.
  public resizeStart = output<PointerEvent>();
  public resizeMove = output<PointerEvent>();
  public resizeEnd = output<PointerEvent>();
  public resizeReset = output<void>();

  /** BoardPanelHost.refresh — the board owns the queries, so this only forwards the request. */
  public refreshRequested = output<BoardPanelRefreshScope>();
  public rowPatchRequested = output<BoardPanelRowPatch>();
  /** BoardPanelHost.upsertRecord — the board owns the page, so this only forwards the saved record. */
  public recordUpserted = output<unknown>();
  /** The board owns the frame switch and the editors, so the panel only reports the click. */
  public expandRequested = output<void>();
  public dockRequested = output<void>();
  /** The board owns row order and the dirty-edit flush, so the panel only reports the direction. */
  public stepRequested = output<1 | -1>();

  /**
   * Whether this frame draws title/pin/close. Public as `BoardPanelHost.$ownsHeader` so Detail can
   * keep its w-page header (and pin) instead of stacking a second bar.
   */
  public readonly $ownsHeader = computed(() => this.$ownsHeaderInput() ?? this.$kind() === 'quickview');

  public readonly $pinAvailable = computed(() => this.$kind() === 'quickview' && this.$presentation() === 'side');

  protected readonly $hold = computed(() => surfaceBusyTooltip(this.$busy()));

  protected readonly $stepLabel = computed(() => {

    const position = this.$stepPosition();
    return position ? `${position.index} of ${position.total}` : null;
  });

  public readonly $canPin = computed(() => this.$resizable());

  protected readonly $pinDisabled = computed(() => !this.$canPin());

  protected readonly $pinIcon = computed(() => this.$pinned() ? 'fa-solid fa-thumbtack' : 'fa-regular fa-thumbtack');

  protected readonly $pinTooltip = computed(() => {
    if (this.$pinDisabled()) return 'Not enough room to pin';
    return this.$pinned() ? 'Unpin panel' : 'Pin panel open';
  });

  public constructor() {

    DesignSystem.Mount(this.document, boardPanelStyleId, boardPanelBridgeStyles);

    // Window capture runs before the inspector's document capture. Without this, Escape closes the
    // inspector while Task Details stays up. Palette is registered on window first and consumes
    // when open; overlay / entity dialogs are higher still — yield so they keep the key.
    const onKeydown = (event: KeyboardEvent) => this.onWindowEscape(event);
    this.document.defaultView?.addEventListener('keydown', onKeydown, true);
    this.destroyRef.onDestroy(() => this.document.defaultView?.removeEventListener('keydown', onKeydown, true));

    // Overlay spacer must match the painted panel, not the declared px width. A floating panel is
    // `min(--board-panel-width, 100%)`, so maxWidth (520) over-reserves and hides row avatars.
    afterNextRender(() => {

      const el = this.hostEl.nativeElement.querySelector('.board-panel');
      // A dialog frame outlives the side panel that replaces it (leave animation); clearing on its
      // destroy would wipe the side panel's spacer.
      if (!(el instanceof HTMLElement) || this.$presentation() === 'dialog') return;
      this.reportOverlayInset(el);
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => this.zone.run(() => this.reportOverlayInset(el)));
      observer.observe(el);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        this.panelLayout.setPanel(false, this.$pinned(), 0);
      });
    });

    effect(() => {
      this.$pinned();
      untracked(() => {
        const el = this.hostEl.nativeElement.querySelector('.board-panel');
        if (el instanceof HTMLElement) this.reportOverlayInset(el);
      });
    });
  }

  private reportOverlayInset(el: HTMLElement): void {

    // A dialog sits over the whole board, so it reserves no overlay spacer in the grid.
    if (this.$presentation() === 'dialog') return;
    const width = Math.round(el.getBoundingClientRect().width);
    if (width <= 0) return;
    this.panelLayout.setPanel(true, this.$pinned(), width);
  }

  private onWindowEscape(event: KeyboardEvent): void {

    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (this.$kind() !== 'quickview') return;
    const dialog = this.document.querySelector('entity-dialog-host');
    if (dialog && !dialog.contains(this.hostEl.nativeElement)) return;
    if (this.document.querySelector('.wb-dialog-overlay-pane')) return;
    // WBS-12997: the docked form fill is no overlay pane, so without this the panel closes and the fill never sees Escape.
    if (this.document.querySelector('task-form-fill-dialog')) return;
    if (Array.from(this.document.querySelectorAll('.cdk-overlay-pane')).some(pane => pane.childElementCount > 0)) return;
    const panels = Array.from(this.document.querySelectorAll('board-panel.is-quickview'));
    if (panels.at(-1) !== this.hostEl.nativeElement) return;
    if ((event.target as HTMLElement | null)?.closest?.('wb-field.is-dirty:not(.is-saving)')) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.onClose();
  }

  // ── BoardPanelHost ────────────────────────────────────────────────────────
  // Public because mounted content injects this component through BOARD_PANEL_HOST. $pinned and
  // $ownsHeader are declared above and satisfy the readonly Signals on the interface.

  public setPinned(pinned: boolean): void {

    if (pinned && !this.$canPin()) return;
    this.pinnedChange.emit(pinned);
  }

  public close(): void {

    this.onClose();
  }

  public refresh(scope: BoardPanelRefreshScope = 'record'): void {

    this.refreshRequested.emit(scope);
  }

  public patchRow(rowId: string, patch: Record<string, unknown>): void {

    this.rowPatchRequested.emit({ rowId, patch });
  }

  public upsertRecord(record: unknown): void {

    this.recordUpserted.emit(record);
  }

  protected onClose(): void {

    this.closed.emit();
  }

  protected onTogglePin(): void {

    this.setPinned(!this.$pinned());
  }

  protected onStep(direction: 1 | -1): void {

    this.step(direction);
  }

  /** BoardPanelHost.step — editor-owned headers (Detail) call this; the board owns the guard and row order. */
  public step(direction: 1 | -1): void {

    this.stepRequested.emit(direction);
  }

  /** BoardPanelHost.expand — same report as the panel's own ⤢. */
  public expand(): void {

    if (this.$expand().kind) this.expandRequested.emit();
  }

  public dock(): void {

    if (this.$expand().canDock) this.dockRequested.emit();
  }

  /** `noopener`: the new tab must not be able to navigate this one. */
  public openRecordPage(): void {

    const url = this.$recordPageUrl();
    const view = this.document.defaultView;
    if (!url || !view) return;
    view.open(boardPanelRecordPageHref(url, view.location.origin, path => this.location.prepareExternalUrl(path)), '_blank', 'noopener');
  }

}
